import { Schema as S } from "effect";

/** A client-minted 32-byte recovery secret encoded as lowercase hexadecimal.
 * @since 1.1.8
 * @example S.decodeUnknownSync(RecoveryReceiptSchema)("ab".repeat(32));
 */
export const RecoveryReceiptSchema = S.String.pipe(S.check(S.isPattern(/^[0-9a-f]{64}$/)));

/** Status authenticates a recorded submission with its receipt, never a caller patient ID.
 * @since 1.1.8
 * @example const request: BookingStatusRequest = { clientSubmissionId: "submission-1", recoveryReceipt: "ab".repeat(32) };
 */
export const BookingStatusRequestSchema = S.Struct({
  clientSubmissionId: S.String.pipe(S.check(S.isMinLength(1))),
  recoveryReceipt: RecoveryReceiptSchema,
});
/** Typed receipt-authenticated status input.
 * @since 1.1.8
 * @example const request: BookingStatusRequest = { clientSubmissionId: "submission-1", recoveryReceipt: "ab".repeat(32) };
 */
export type BookingStatusRequest = S.Schema.Type<typeof BookingStatusRequestSchema>;

const RecordedBinding = S.Struct({
  patientId: S.String,
  appointmentTypeId: S.String,
  calendarId: S.String,
  startTime: S.String,
  endTime: S.String,
});

/** Bounded recovery outcomes exclude patient notes and questionnaire answers.
 * Unknown or expired receipts disclose no binding; confirmed results are shown only
 * after the terminal compares the recorded patient with its current identification.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingStatusResponseSchema)({ status: "expired" });
 */
export const BookingStatusResponseSchema = S.Union(
  [S.Struct({ status: S.Literal("unknown") }),
  S.Struct({ status: S.Literal("expired") }),
  S.Struct({ status: S.Literals(["prepared", "dispatching", "ambiguous", "rejected"]), binding: RecordedBinding }),
  S.Struct({ status: S.Literal("created"), binding: RecordedBinding, scheduleId: S.String }),
  S.Struct({ status: S.Literal("verified"), binding: RecordedBinding, scheduleId: S.String })],
);
/** Typed bounded recovery outcome.
 * @since 1.1.8
 * @example const response: BookingStatusResponse = { status: "expired" };
 */
export type BookingStatusResponse = S.Schema.Type<typeof BookingStatusResponseSchema>;

/** A receipt must be supplied before any newly prepared booking dispatch.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingPreparationSchema)({ clientSubmissionId: "submission-1", recoveryReceipt: "ab".repeat(32) });
 */
export const BookingPreparationSchema = BookingStatusRequestSchema;

/** Typed recovery failures avoid reinterpreting patient-facing messages as retry authority.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingRecoveryErrorSchema)({ _tag: "BookingRecoveryError", reason: "storage-unavailable" });
 */
export const BookingRecoveryErrorSchema = S.TaggedStruct("BookingRecoveryError", {
  reason: S.Literals(["storage-unavailable", "intent-mismatch", "receipt-mismatch", "expired", "outcome-unknown"]),
});
